/*
 * @format
 */
import moment from 'moment';
import { convertToSiTime } from './utils/helpers';
import { MAX_SETPOINT, MODES } from './utils/consts';
import { isMatchingZoneId } from './utils/helpers';
import { getPendingData } from './reducers/pending-reducer';

import {
  getCurrentEvent,
  getCurrentScheduleSetpoint,
} from './selectors/schedule-selectors';
import {
  getSharedPending,
  getSharedHolidayPending,
} from './selectors/desired-state-selectors';
import {
  isZoneOnline,
  getChZones,
  getChZoneForDhwZone,
  getServiceModeAlarm,
} from './selectors';

const pendingErrors = state => [
  ...state.pendingSetpoint.errors,
  ...state.pendingMode.errors,
  ...state.pendingHolidayMode.errors,
  ...state.pendingPublish.errors,
];

export const getFirstPendingError = state => {
  const p = pendingErrors(state);
  return p.length > 0 ? p[0] : undefined;
};

export const getPendingErrors = state => {
  const p = pendingErrors(state);
  return p.reduce((acc, curr) => {
    if (acc.find(x => x.requestId === curr.requestId)) {
      return acc;
    }
    acc.push(curr);
    return acc;
  }, []);
};

export const havePendingErrors = state => pendingErrors(state).length > 0;

export const getPrtSettingsError = state => {
  return state.settings.error ? [state.settings.error] : [];
};

export const getZone = (state, homeId, zoneId) => {
  const home = state.homeList.homes.find(x => x.id === homeId);
  const zone = home?.zones?.find(x => isMatchingZoneId(x.zoneId, zoneId));

  return zone;
};

export const isBoilerHeating = (state, homeId, zoneId) => {
  const home = state.homeList.homes.find(x => x.id === homeId);
  const zone = home.zones.find(x => isMatchingZoneId(x.zoneId, zoneId));

  const setpoint = getSetpoint(state, homeId, zoneId);

  // logic override to hide flame if target temp is below room temp [DV-2091]
  let result = 'off';
  if (zone.flame === true && zone.optimisedStartActive) {
    result = 'optimised';
  } else if (zone.flame === true) {
    result = 'on';
  } else {
    result = 'off';
  }

  if (zone.temperature > setpoint) {
    result = 'off';
  }

  return result;
};

export const isHeatPumpHeating = (state, homeId, zoneId) => {
  const { setpoint, temperature, optimisedStartActive } = getZone(
    state,
    homeId,
    zoneId,
  );

  if (setpoint > temperature) {
    return 'on';
  }

  if (optimisedStartActive) {
    return 'optimised';
  }

  return 'off';
};

export const getUpperLimitLock = (state, homeId, zoneId) => {
  // Maximum setpoint allowed as configured by the landlord (DV-2448)
  const zone = getZone(state, homeId, zoneId);
  if (zone && zone.upperLimitLock) {
    return zone.upperLimitLock;
  }
  return null;
};

export const getLowerLimitLock = (state, homeId, zoneId) => {
  // Minimum setpoint allowed as configured by the landlord (DV-2448)
  const zone = getZone(state, homeId, zoneId);
  if (zone && zone.lowerLimitLock) {
    return zone.lowerLimitLock;
  }
  return null;
};

export const getFrostProtectionSetpoint = (state, homeId, zoneId) => {
  // if you have one, you get it back - if not you get default 500
  const zone = getZone(state, homeId, zoneId);
  if (zone && zone.frostProtectionSetpoint) {
    return zone.frostProtectionSetpoint;
  }
  return 500;
};

export const isInHolidayMode = (state, homeId, zoneId) => {
  const zone = getChZoneForDhwZone(state, homeId, zoneId);
  const pendingHoliday = getPendingData(
    state.pendingHolidayMode,
    homeId,
    zoneId,
  );

  if (!zone) {
    return 2;
  }

  if (pendingHoliday) {
    return pendingHoliday.holidayMode === 3;
  }

  // A shared desired "into holiday" goal (this client's or a peer's) puts the
  // zone in holiday before the device reports holidayMode === 3, so an observing
  // phone matches the initiator and controls are disabled meanwhile. Keyed on the
  // CH zone the goal is authored against (getChZoneForDhwZone maps a DHW zone to
  // it above), between local pending and reported — the same precedence getMode
  // uses for the heating axis.
  if (getSharedHolidayPending(state, homeId, zone.zoneId)) {
    return true;
  }

  const { holidayMode } = zone;

  return holidayMode === 3;
};

export const isReallyInHolidayMode = (state, homeId, zoneId) => {
  const zone = getZone(state, homeId, zoneId);

  const { holidayMode } = zone;

  return holidayMode === 3;
};

export const controlsDisabled = (state, homeId, zoneId) => {
  const pendingHoliday = getPendingData(
    state.pendingHolidayMode,
    homeId,
    zoneId,
  );

  const holidayMode = isInHolidayMode(state, homeId, zoneId);

  // if a holidayMode is still active keep controls disabled
  if (holidayMode) {
    return true;
  }

  if (pendingHoliday) {
    const pendingHolidayKeys = Object.keys(pendingHoliday);
    const pendingHasHolidayModeKey = pendingHolidayKeys?.find(
      key => key === 'holidayMode',
    );

    // if the pending holiday data has not got the holidayMode key it is a cancelHoliday message
    if (pendingHolidayKeys.length > 0 && !pendingHasHolidayModeKey) {
      // if the pending holiday data has the requestId and it equals "done" return false e.g. controlsDisabled = false
      // if the pending holiday data has the requestId and it doesn't equal "done" return true e.g. controlsDisabled = true
      return pendingHoliday.requestId !== 'done';
    }
  }

  // if both holidayMode and pendingHoliday are falsy return false e.g. controlsDisabled = false
  return false;
};

// The effective mode for display and command anchoring: this client's own
// optimistic pending wins, then another client's accepted target (shared
// desired state), then the last reported mode. Shared desired sits between own
// pending and reported because it is the same class of value as our own pending
// — an accepted target the reconciler is committed to driving the device to —
// just originated by a different client. getSharedPending already stands down
// for our own goals and whenever a local optimistic value is rendering.
export const getMode = (state, homeId, zoneId) => {
  if (typeof state !== 'undefined') {
    const pendingState = getPendingData(state.pendingMode, homeId, zoneId);
    if (pendingState && pendingState.mode) {
      return pendingState.mode;
    }
    const shared = getSharedPending(state, homeId, zoneId);
    if (shared && shared.mode) {
      return shared.mode;
    }
    const zone = getZone(state, homeId, zoneId);
    return zone ? zone.mode : null;
  }
  return null;
};

// Mode from this client's own frame — local optimistic pending over reported,
// WITHOUT the shared-desired overlay. Command paths that must anchor to what
// THIS client actually did (or the device actually reports) — never a peer's
// in-flight goal — use this. In particular it captures previousMode for a
// DSR-cancel restore, where the restore target must be the real prior mode, not
// another client's unconfirmed target.
export const getBaseMode = (state, homeId, zoneId) => {
  if (typeof state !== 'undefined') {
    const pendingState = getPendingData(state.pendingMode, homeId, zoneId);
    if (pendingState && pendingState.mode) {
      return pendingState.mode;
    }
    const zone = getZone(state, homeId, zoneId);
    return zone ? zone.mode : null;
  }
  return null;
};

export const getSetpoint = (state, homeId, zoneId) => {
  const pendingState = getPendingData(state.pendingSetpoint, homeId, zoneId);
  let nextSetpointValue = null;

  const zone = getZone(state, homeId, zoneId);

  if (pendingState && typeof pendingState.setpoint === 'number') {
    // this client's own optimistic value wins
    nextSetpointValue = pendingState.setpoint;
  }

  if (nextSetpointValue == null) {
    // another client's accepted target (below our own pending, above reported).
    // A mode-only goal (e.g. Auto) carries no setpoint on the wire, so resolve
    // it from the schedule exactly as the local switch-to-Auto path does — the
    // observing client then shows the same value the initiating client shows.
    const shared = getSharedPending(state, homeId, zoneId);
    if (shared) {
      if (typeof shared.setpoint === 'number') {
        nextSetpointValue = shared.setpoint;
      } else if (shared.mode === MODES.Auto) {
        nextSetpointValue = getCurrentScheduleSetpoint(
          state,
          homeId,
          zoneId,
          new Date(),
        );
      }
    }
  }

  if (nextSetpointValue == null) {
    // last reported
    nextSetpointValue = zone.setpoint;
  }

  const frostProtectSetpoint = getFrostProtectionSetpoint(
    state,
    homeId,
    zoneId,
  );

  const mode = getMode(state, homeId, zoneId);

  if (isInHolidayMode(state, homeId, zoneId) || mode === MODES.Off) {
    return frostProtectSetpoint;
  }

  if (nextSetpointValue != null) {
    if (nextSetpointValue <= frostProtectSetpoint) {
      return frostProtectSetpoint; // handle low out of range value
    }
    if (nextSetpointValue > MAX_SETPOINT) {
      return MAX_SETPOINT; // handle high out of range value
    }
    return nextSetpointValue; // otherwise return fine
  }
  return null;
};

export const getMinSetpoint = (state, homeId, zoneId) => {
  const defaultMin = 500;

  const zone = getZone(state, homeId, zoneId);
  const { lowerLimitLock } = zone;

  const frostProtectSetpoint = getFrostProtectionSetpoint(
    state,
    homeId,
    zoneId,
  );

  if (
    frostProtectSetpoint &&
    frostProtectSetpoint > lowerLimitLock &&
    frostProtectSetpoint > defaultMin
  ) {
    return frostProtectSetpoint;
  }

  if (lowerLimitLock > defaultMin) {
    return lowerLimitLock;
  }

  return defaultMin;
};

export const getMaxSetpoint = (state, homeId, zoneId) => {
  const defaultMax = 3000;

  const zone = getZone(state, homeId, zoneId);
  const { upperLimitLock } = zone;

  let maxSetpoint = defaultMax;

  if (upperLimitLock < defaultMax) {
    maxSetpoint = upperLimitLock;
  }

  const serviceModeAlarm = getServiceModeAlarm(state, homeId, zoneId);
  if (
    serviceModeAlarm &&
    zone.serviceModeType === 2 &&
    typeof zone.serviceModeDaysRemaining === 'number' &&
    zone.serviceModeDaysRemaining <= 0 &&
    zone.serviceModeSetpoint != null
  ) {
    maxSetpoint = Math.min(maxSetpoint, zone.serviceModeSetpoint);
  }

  return maxSetpoint;
};

export const getComfortSetpoint = (state, homeId, zoneId) => {
  const zone = getZone(state, homeId, zoneId);

  return zone?.comfortTemperatureSetpoint;
};

export const getPreviousTemperatureMode = (state, homeId, zoneId) => {
  const zone = getZone(state, homeId, zoneId);
  if (zone) {
    return zone.previousTemperatureMode;
  }
  return null;
};

// not used for geolocation
export const getPreviousMode = (state, homeId, zoneId) => {
  const pendingState = getPendingData(state.pendingMode, homeId, zoneId);

  if (pendingState && pendingState.previousMode) {
    return pendingState.previousMode;
  }

  const zone = getZone(state, homeId, zoneId);

  if (zone) {
    return zone.previousMode;
  }

  return null;
};

export const getPreviousSetPoint = (state, homeId, zoneId) => {
  const zone = getZone(state, homeId, zoneId);
  if (zone) {
    return zone.previousSetpoint;
  }
  return null;
};

// ignores the pending state - used for debug users to
// see what we are being sent by the si
export const getLastReportedZoneData = (state, homeId, zoneId) => {
  const zone = getZone(state, homeId, zoneId);

  if (zone) {
    return {
      setpoint: zone.setpoint,
      mode: zone.mode,
      holidayMode: zone.holidayMode,
    };
  }

  return {
    setpoint: 0,
    mode: 'unknown',
  };
};

export const getPlusHoursEndtime = (state, homeId, zoneId) => {
  const mode = getMode(state, homeId, zoneId);
  if (mode === MODES.OverridePlusHours || mode === MODES.DSRActive) {
    // stop us getting into this state when we're not overriding
    const pendingState = getPendingData(state.pendingMode, homeId, zoneId);

    const siNowTime = convertToSiTime(Date.now());

    if (
      pendingState &&
      pendingState.overrideEndtime &&
      convertToSiTime(pendingState.overrideEndtime).isAfter(siNowTime)
    ) {
      return convertToSiTime(pendingState.overrideEndtime);
    }

    const zone = getZone(state, homeId, zoneId);

    if (zone && convertToSiTime(zone.overrideEndtime).isAfter(siNowTime)) {
      return convertToSiTime(zone.overrideEndtime);
    }

    // The effective mode may come from another client's accepted
    // OverridePlusHours goal, which carries a relative holdDuration (minutes)
    // rather than an absolute overrideEndtime. The device has not applied the
    // boost yet (it is still in the reported mode handled above), so the boost
    // will run its full holdDuration from when it applies (~now); estimate the
    // end as now + holdDuration. This is never null, so a dial change keeps the
    // zone in OverridePlusHours with a valid duration instead of emitting a NaN.
    // Once the device applies the boost and reports its own overrideEndtime, the
    // branch above takes over and the countdown ticks down normally.
    const shared = getSharedPending(state, homeId, zoneId);
    if (shared && typeof shared.holdDuration === 'number' && shared.holdDuration > 0) {
      return convertToSiTime(Date.now() + shared.holdDuration * 60000);
    }

    return null;
  }
  return null;
};

export const getPlusHoursTimeRemaining = (state, homeId, zoneId) => {
  const plusHoursEndtime = getPlusHoursEndtime(state, homeId, zoneId);

  if (!plusHoursEndtime) {
    return undefined;
  }

  const now = convertToSiTime(Date.now());
  const diff = plusHoursEndtime.diff(now, 'minutes');

  if (diff <= 0) {
    return undefined;
  }

  return diff;
};

export const getPendingDebugData = (state, homeId, zoneId) => {
  const pendingModeData = getPendingData(state.pendingMode, homeId, zoneId);
  const pendingSetpointData = getPendingData(
    state.pendingSetpoint,
    homeId,
    zoneId,
  );

  return [pendingModeData, pendingSetpointData];
};

export const getHolidayDuration = (state, homeId, zoneId) => {
  const zone = getZone(state, homeId, zoneId);
  const pendingHoliday = getPendingData(
    state.pendingHolidayMode,
    homeId,
    zoneId,
  );

  let holidayEndTime;
  if (pendingHoliday) {
    holidayEndTime = pendingHoliday.holidayEndTime;
  } else {
    // A shared desired holiday carries its accepted end time before the device
    // reports holidayModeEnd, so an observing phone shows the right duration
    // rather than a stale/blank reported value. Keyed on the CH zone the goal is
    // authored against (matches isInHolidayMode); reported is the final fallback.
    const chZone = getChZoneForDhwZone(state, homeId, zoneId);
    const sharedHoliday =
      chZone && getSharedHolidayPending(state, homeId, chZone.zoneId);
    holidayEndTime = sharedHoliday
      ? sharedHoliday.holidayEndTime
      : zone.holidayModeEnd;
  }

  return moment(holidayEndTime).diff(moment(), 'days');
};

// Order two reported end times by the instant they name rather than by how they
// are spelled. A default .sort() is lexicographic, and these values do not share
// one shape: the device reports '2022-10-20T13:16Z' (no seconds, no millis)
// alongside full ISO strings, and a non-'Z' offset sorts entirely wrongly
// ('2026-09-26T01:00-05:00' is 06:00Z, but sorts before '...T03:00:00.000Z').
// A blank or unparseable end time sorts last so a zone that does report one
// wins; when every zone is blank the '' still surfaces, and callers read that as
// "in holiday" with no date to show.
const holidayInstant = value => {
  const parsed = value ? moment(value) : null;

  return parsed?.isValid() ? parsed.valueOf() : Number.POSITIVE_INFINITY;
};

const byHolidayInstant = (a, b) => {
  const first = holidayInstant(a);
  const second = holidayInstant(b);

  // Not a subtraction: two unparseable values are both Infinity, and
  // Infinity - Infinity is NaN, which makes the sort order undefined.
  if (first === second) {
    return 0;
  }

  return first < second ? -1 : 1;
};

export function getNearestHolidayEndTime(state, homeId) {
  const endTime = getChZones(state, homeId)
    .map(zone => {
      // Local optimistic holiday wins (as before: pending-or-reported). A shared
      // desired holiday goal — between local pending and reported, matching
      // isInHolidayMode — puts the zone in holiday before the device reports
      // holidayMode === 3, so the Property holiday row updates meanwhile. It is
      // represented as a holiday-in zone-like entry so the existing filter/map
      // below is unchanged (including its handling of a blank end time, which
      // callers read as "in holiday" via `!== undefined`, not truthiness).
      const pending = getPendingData(
        state.pendingHolidayMode,
        homeId,
        zone.zoneId,
      );
      const shared =
        !pending && getSharedHolidayPending(state, homeId, zone.zoneId);
      if (shared) {
        return { holidayMode: 3, holidayModeEnd: shared.holidayEndTime };
      }
      return pending || zone;
    })
    .filter(x => x.holidayMode === 3)
    .map(x => x.holidayModeEnd)
    .sort(byHolidayInstant);

  if (endTime.length === 0) {
    return undefined;
  }

  return endTime[0];
}

export const isFrostProtectionActive = (state, homeId, zoneId) => {
  const mode = getMode(state, homeId, zoneId);
  const setpoint = getSetpoint(state, homeId, zoneId);
  const zone = getZone(state, homeId, zoneId);

  if (!zone) {
    return true;
  }

  return (
    mode === MODES.Off ||
    setpoint <= getFrostProtectionSetpoint(state, homeId, zoneId)
  );
};

export const isAutoOff = (state, homeId, zoneId, inHolidayMode) => {
  if (inHolidayMode) {
    return false;
  }

  const mode = getMode(state, homeId, zoneId);
  const setpoint = getSetpoint(state, homeId, zoneId);
  const zone = getZone(state, homeId, zoneId);

  if (!zone) {
    return false;
  }

  return (
    mode === MODES.Auto &&
    setpoint <= getFrostProtectionSetpoint(state, homeId, zoneId)
  );
};

// this gets the whether the display mode is On/Off
export function getDhwActiveMode(state, homeId, zoneId) {
  const zone = getZone(state, homeId, zoneId);

  const dhwTypes = ['DHW', 'HPI_DHW'];

  if (!zone || !dhwTypes.includes(zone?.type)) {
    return '';
  }

  const mode = getMode(state, homeId, zoneId);

  if (isInHolidayMode(state, homeId, zoneId) || mode === MODES.Off) {
    return MODES.Off;
  }
  const currentEvent = getCurrentEvent(state, homeId, zoneId);
  const isAutoOffDhw = !currentEvent?.on;

  if (mode === MODES.Auto) {
    if (isAutoOffDhw) {
      return MODES.Off;
    }

    return MODES.On;
  }

  if (mode === MODES.OverrideNext || mode === MODES.OverridePlusHours) {
    return MODES.On;
  }

  return mode;
}

export const isMaxSetpoint = setpoint => setpoint === MAX_SETPOINT;

const getLastCompletedMessage = (state, homeId, [zoneId, endpointId]) => {
  const home = state.completedMessages[homeId];
  if (!home) {
    return null;
  }

  const zone = home[zoneId];

  if (!zone) {
    return null;
  }

  const ep = zone[endpointId];

  if (!ep) {
    return null;
  }

  return ep.last;
};

export const getCompletedStatusText = (state, homeId, zoneId) => {
  const message = getLastCompletedMessage(state, homeId, zoneId);

  if (message && message.command) {
    switch (message.command) {
      case 'cancelOverride':
        return zoneId[1] === 6 ? 'Boost Cancelled' : 'Override Cancelled';
      default:
        return zoneId[1] === 6 ? 'Hot Water set to' : 'Heating set to';
    }
  }
  return zoneId[1] === 6 ? 'Hot Water set to' : 'Heating set to';
};

export const isCloudSupported = (state, homeId) => {
  if (!homeId) {
    return false;
  }
  const home = state.homeList.homes.find(x => x.id === homeId);

  if (!home) {
    return false;
  }

  if (home.cloudSupported === false) {
    return false;
  }

  return true;
};

export const canSettingsBeSent = (state, homeId, zoneId) => {
  const deviceOffline = !isZoneOnline(state, homeId, zoneId);
  if (deviceOffline) {
    return false;
  }

  const holidayModeIsActive = isInHolidayMode(state, homeId, zoneId);
  if (holidayModeIsActive) {
    return false;
  }

  const cloudSupported = isCloudSupported(state, homeId);
  if (!cloudSupported) {
    // block options if no cloud license
    return false;
  }

  if (controlsDisabled(state, homeId, zoneId)) {
    return false;
  }

  const mode = getMode(state, homeId, zoneId);

  switch (mode) {
    case MODES.On:
    case MODES.Off:
    case MODES.Manual:
    case MODES.Auto:
      return true;

    default:
      return false;
  }
};

export const waitingForMessage = (state, homeId) => {
  const home = state.modeChangeTracker[homeId];

  if (!home) {
    return false;
  }

  return home;
};

export const waitingForMessageFromAny = state => {
  return Object.values(state.modeChangeTracker).some(s => s === true);
};

export function deviceServiceRequestSending(state, homeId) {
  if (homeId) {
    return state.deviceService[homeId] !== undefined;
  } else {
    // return false;
    return Object.keys(state.deviceService).filter(x => !!x).length > 0;
  }
}

// Whether a control command for this home (any home, when homeId is omitted) is
// still in flight — the signal behind the header "Sending request" / "Updating
// information" cues. Their job is to tell the user their change is still on its
// way, so they know when it is safe to leave the app (or close the tab). It is
// NOT a lock: darwin_core takes a new command on top of one still out — the send
// queue debounces and queues per home, the device-service sender keeps the
// latest request, and the pending slices replace the zone's request — so
// callers should never disable inputs on this.
//
// There is one "request in flight" tracker per transport, both per HOME because
// a home's commands are serialised together, and only one is ever populated for
// a given home:
//   - deviceService (device-service homes: haloDeviceServiceEnabled, or any heat
//     pump). Set when the desired-state envelope is handed to the debounced
//     sender; cleared by the cloud's commandStatus ack/nak for that requestId,
//     or the 30s client timeout. ("Sending request")
//   - modeChangeTracker (the legacy setPrtMode path over the controls saga).
//     True from the saga taking the command until its queue drains: the
//     device's completion frame AND a live report matching the commanded state,
//     or the 20s saga timeout. ("Updating information")
//
// This is deliberately NOT derived from the pendingMode / pendingSetpoint
// slices. Those carry the optimistic VALUE, and an entry only leaves "pending"
// on the device's legacy completion frame (setPrtMode… with state 'complete')
// or a timeout; nothing in the desired-state flow marks one done, so a spinner
// keyed on them runs the full 30s timeout on a device-service home.
export function isCommandInFlight(state, homeId) {
  if (deviceServiceRequestSending(state, homeId)) {
    return true;
  }

  return homeId
    ? !!waitingForMessage(state, homeId)
    : waitingForMessageFromAny(state);
}

export const getlatestSchedule = (state, homeId, zoneId) => {
  const zone = getZone(state, homeId, zoneId);

  if (zone) {
    return {
      schedule: zone.schedule,
    };
  }

  return {
    schedule: [],
  };
};

export const isHpi = (state, homeId, zoneId) => {
  const zone = getZone(state, homeId, zoneId);

  if (zone) {
    return zone.type === 'HPI_CH' || zone.type === 'HPI_DHW';
  }

  return false;
};

export const isDhw = (state, homeId, zoneId) => {
  const zone = getZone(state, homeId, zoneId);

  if (zone) {
    return zone.type === 'DHW' || zone.type === 'HPI_DHW';
  }

  return false;
};
